import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import cx from 'classnames'
import { HTMLInputEvent, SetState, AlmanacFile as File } from 'types'
import Styles from './RenameInput.module.scss'
import Foco from 'react-foco'
import { stopPropagation } from 'utils'
import { useKey } from 'react-use'
import { SelectPropertyEventDetails, selectPropertyEventName } from '../FileTree/events/selectProperty'

interface RenameInputProps {
  file: File
  isBranch?: boolean
  onRename: (file: File, newName: string) => void
  setShowRename: SetState<boolean>
  className?: string
  noOutline?: boolean
  isEditing?: boolean
}

export type RenameInputHandles = {
  clear: () => void
}

export const RenameInput = forwardRef<RenameInputHandles, RenameInputProps>((props, ref) => {
  const { setShowRename, file, onRename, className, noOutline = false, isEditing } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const currentTitle = file.title
  const [titleInput, setTitleInput] = useState(file.title)

  useImperativeHandle(ref, () => ({
    clear: () => {
      setTitleInput('')
    },
  }))

  useEffect(() => {
    setTitleInput(file.title)
  }, [file.title])

  useKey(
    'Tab',
    (e) => {
      if (e.target !== inputRef.current) return
      setShowRename(false)
      document.body.dispatchEvent(
        new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
          bubbles: false,
          detail: e.shiftKey ? 'Previous' : 'Next',
        }),
      )
    },
    undefined,
    [inputRef.current],
  )

  useKey(
    'Enter',
    (e) => {
      if (!inputRef.current) return

      e.preventDefault()
      if (inputRef.current?.value !== currentTitle && inputRef.current?.value !== '') {
        onRename(file, titleInput)
      }
      setShowRename(false)
      document.body.dispatchEvent(
        new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
          bubbles: false,
          detail: 'Down',
        }),
      )
    },
    undefined,
    [inputRef.current, currentTitle, onRename, file, titleInput, setShowRename],
  )

  useKey(
    'Escape',
    (e) => {
      if (!isEditing) return false
      e.stopPropagation()
      e.stopImmediatePropagation()
      setShowRename(false)
      setTitleInput(file.title)
    },
    { options: true },
    [setShowRename, isEditing],
  )

  const handleClickOutside = () => {
    if (inputRef.current?.value !== currentTitle && inputRef.current?.value !== '') {
      onRename(file, titleInput)
    }

    setShowRename(false)
  }

  if (!isEditing) return null

  return (
    <Foco onClickOutside={handleClickOutside} className={className}>
      <div className={cx(Styles.renameInput, { [Styles.outline]: !noOutline })} onMouseDown={stopPropagation}>
        <input
          autoFocus
          id={file.id}
          ref={inputRef}
          value={titleInput}
          onBlur={() => setShowRename(false)}
          onChange={(e: HTMLInputEvent) => setTitleInput(e.target.value)}
          data-testid="rename-file-input"
        />
      </div>
    </Foco>
  )
})
RenameInput.displayName = 'RenameInput'
